import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    company_name: "",
    company_number: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "Genève",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé. En attente de validation par l'admin.");
      navigate("/pending");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="register-page">
      <div className="ff-container py-12">
        <Link to="/">
          <img src={LOGO_URL} alt="FineFood Primeur" className="h-14" />
        </Link>
      </div>
      <div className="ff-container pb-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="ff-overline">Inscription B2B</p>
          <h1 className="font-serif text-4xl lg:text-5xl mt-3 leading-tight">
            Créez votre<br />compte professionnel.
          </h1>
          <p className="mt-6 text-[#4A5D4E] leading-relaxed">
            Pour garantir la qualité de notre service B2B, chaque demande est validée
            manuellement par notre équipe sous 24h ouvrées.
          </p>
          <div className="mt-10 space-y-4 text-sm text-[#4A5D4E] border-l-2 border-[#2D6A4F] pl-6">
            <p><strong className="text-[#1A2B1E]">1.</strong> Remplissez le formulaire ci-contre.</p>
            <p><strong className="text-[#1A2B1E]">2.</strong> Notre équipe vérifie votre activité.</p>
            <p><strong className="text-[#1A2B1E]">3.</strong> Vous recevez vos accès au catalogue pro.</p>
          </div>
        </div>

        <div className="md:col-span-7">
          <form onSubmit={onSubmit} className="bg-white border border-[#E5E3DC] p-8 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Prénom" required value={form.first_name} onChange={set("first_name")} testid="register-firstname" />
              <Field label="Nom" required value={form.last_name} onChange={set("last_name")} testid="register-lastname" />
            </div>
            <Field label="Nom de l'entreprise" required value={form.company_name} onChange={set("company_name")} testid="register-company" />
            <Field label="Numéro d'entreprise (CHE / IDE)" required value={form.company_number} onChange={set("company_number")} testid="register-company-number" placeholder="CHE-123.456.789" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Téléphone" required value={form.phone} onChange={set("phone")} testid="register-phone" placeholder="+41 ..." />
              <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="register-email" />
            </div>
            <Field label="Mot de passe (min. 6 caractères)" type="password" required value={form.password} onChange={set("password")} testid="register-password" />
            <Field label="Adresse" required value={form.address} onChange={set("address")} testid="register-address" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Code postal" required value={form.postal_code} onChange={set("postal_code")} testid="register-postal" />
              <Field label="Ville" required value={form.city} onChange={set("city")} testid="register-city" />
            </div>

            {error && (
              <div className="text-sm text-[#D62828] bg-[#D62828]/10 p-3 border border-[#D62828]/30" data-testid="register-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="ff-btn-primary w-full justify-center" data-testid="register-submit-button">
              {loading ? "Envoi..." : "Envoyer ma demande"}
            </button>
            <p className="text-xs text-[#8A9A8E] text-center">
              Déjà inscrit ?{" "}
              <Link to="/login" className="text-[#2D6A4F] font-medium hover:underline" data-testid="register-login-link">
                Se connecter
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-[#4A5D4E]">{label}</label>
      <input className="ff-input mt-2" data-testid={testid} {...rest} />
    </div>
  );
}
